import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_painting-prints--3pphcjo7/artifacts/18fe16b393ae9f0a_IMG_2037.JPG";
const LAUNCH_DATE = new Date("2026-12-01T16:00:00+01:00");

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

export default function ComingSoonGate({ onUnlock }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_DATE);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("sending");
    try {
      await axios.post(`${API}/notify`, { email });
      setState("done");
      setEmail("");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="coming-soon-gate">
      <img
        src={HERO_IMG}
        alt="mads i studiet"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ filter: "sepia(0.42) brightness(1.08) saturate(1.12)" }}
      />
      <div className="absolute inset-0 bg-[#F5E8D0]/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#F5E8D0] via-transparent to-[#F5E8D0]/50" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif-display italic text-6xl sm:text-7xl lg:text-8xl tracking-tight text-[#2A2118] drop-shadow-sm"
          data-testid="gate-heading"
        >
          mads<span className="not-italic text-[#C9501C]">.</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 w-full max-w-md"
        >
          <p className="font-serif-display italic text-xl sm:text-2xl text-[#2A2118]" data-testid="gate-subheading">
            The Everybody Looks collection is coming soon
          </p>

          <div className="mt-8 flex items-center justify-center gap-6 sm:gap-10" data-testid="gate-countdown">
            {[
              [days, "days"],
              [hours, "hours"],
              [minutes, "minutes"],
              [seconds, "seconds"],
            ].map(([value, label]) => (
              <div key={label} className="text-center">
                <p
                  className="font-serif-display text-4xl sm:text-5xl text-[#C9501C] tabular-nums"
                  data-testid={`gate-countdown-${label}`}
                >
                  {String(value).padStart(2, "0")}
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[#7A6C5A]">{label}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.25em] text-[#7A6C5A]" data-testid="gate-launch-date">
            Opens December 1st at 4 PM
          </p>

          <p className="mt-6 text-sm text-[#7A6C5A]">
            Leave your email and be the first to know when the site opens.
          </p>

          {state === "done" ? (
            <p className="mt-8 font-serif-display italic text-lg text-[#C9501C]" data-testid="gate-success-message">
              Thank you — you'll hear from us when it opens.
            </p>
          ) : (
            <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="gate-notify-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="gate-email-input"
                className="flex-1 bg-[#FBF3E2]/90 border border-[rgba(42,33,24,0.18)] focus:border-[#C9501C] outline-none px-5 py-4 text-sm text-[#2A2118] placeholder:text-[#AB9C86] transition-colors"
              />
              <button
                type="submit"
                disabled={state === "sending"}
                data-testid="gate-notify-button"
                className="bg-[#C9501C] hover:bg-[#d1553a] disabled:opacity-60 text-[#FBF3E2] font-mono-accent text-xs uppercase tracking-[0.25em] px-6 py-4 transition-colors duration-300"
              >
                {state === "sending" ? "…" : "Notify me"}
              </button>
            </form>
          )}
          {state === "error" && (
            <p className="mt-3 text-xs text-[#C9501C]" data-testid="gate-error-message">
              Something went wrong — please try again.
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
